import secureApiService from './secureApiService'
import {
  LearnerCourseEnrollmentModel,
  LearnerCourseProgressJSON,
  LearnerCourseProgressModel,
  LearnerProgrammeEnrollmentModel,
  LearnerProgrammeEnrollmentResponseJSON,
} from '@/models/learnerCourse.model'
import { ModuleProgressJSON, ModuleProgressModel, ResourceProgressJSON, ResourceProgressModel } from '@/models/course.model'

interface LearnerEnrollmentsResponseJSON {
  success: boolean
  message?: string
  data?:
    | LearnerProgrammeEnrollmentResponseJSON
    | {
        data?: LearnerProgrammeEnrollmentResponseJSON | null
        meta?: Record<string, unknown> | null
      }
    | null
}

interface LearnerCourseProgressResponseJSON {
  success: boolean
  message?: string
  data?: LearnerCourseProgressJSON | null
}

interface ResourceProgressResponseJSON {
  success: boolean
  message?: string
  data?: ResourceProgressJSON | null
}

interface ModuleProgressResponseJSON {
  success: boolean
  message?: string
  data?: ModuleProgressJSON | null
}

export interface LearnerEnrollmentsFilters {
  level?: number
  stage?: number
  page?: number
  perPage?: number
}

export interface RecordResourceProgressPayload {
  playedSeconds?: number
  completed?: boolean
  meta?: Record<string, unknown>
}

export interface CompleteModulePayload {
  timeSpentSeconds?: number
  meta?: Record<string, unknown>
}

class LearnerCoursesService {
  private enrollmentsPath = '/enrollments/me'
  private userCoursePath = '/users/me/courses'

  async getLearnerEnrollments(filters: LearnerEnrollmentsFilters = {}): Promise<LearnerProgrammeEnrollmentModel[]> {
    const searchParams = new URLSearchParams()
    if (filters.level) searchParams.set('level', String(filters.level))
    if (filters.stage) searchParams.set('stage', String(filters.stage))
    if (filters.page) searchParams.set('page', String(filters.page))
    if (filters.perPage) searchParams.set('perPage', String(filters.perPage))

    const queryString = searchParams.toString()
    const endpoint = queryString ? `${this.enrollmentsPath}?${queryString}` : this.enrollmentsPath

    const response = await secureApiService.get<LearnerEnrollmentsResponseJSON>(endpoint, {
      requireAuth: true,
    })

    const payload = response.data
    if (!payload?.success) {
      throw new Error(payload?.message || 'Unable to fetch learner enrollments')
    }

    const programmes = Array.isArray(payload.data)
      ? payload.data
      : Array.isArray(payload.data?.data)
        ? payload.data?.data
        : []

    return programmes
      .filter((programme): programme is LearnerProgrammeEnrollmentResponseJSON[number] => Boolean(programme))
      .map((programme) => LearnerProgrammeEnrollmentModel.fromJSON(programme))
  }

  async getCourseProgress(courseId: string): Promise<LearnerCourseProgressModel> {
    const response = await secureApiService.get<LearnerCourseProgressResponseJSON>(
      `${this.userCoursePath}/${courseId}/progress`,
      { requireAuth: true },
    )

    const payload = response.data
    if (!payload?.success || !payload.data) {
      throw new Error(payload?.message || 'Unable to fetch course progress')
    }

    return LearnerCourseProgressModel.fromJSON(payload.data)
  }

  async recordResourceProgress(
    courseId: string,
    resourceId: string,
    payload: RecordResourceProgressPayload,
  ): Promise<ResourceProgressModel> {
    const response = await secureApiService.post<ResourceProgressResponseJSON>(
      `/courses/${courseId}/resources/${resourceId}/progress`,
      payload,
    )

    const body = response.data
    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to record resource progress')
    }

    return ResourceProgressModel.fromJSON(body.data)
  }

  async completeModule(
    courseId: string,
    moduleId: string,
    payload: CompleteModulePayload,
  ): Promise<ModuleProgressModel> {
    const response = await secureApiService.post<ModuleProgressResponseJSON>(
      `/courses/${courseId}/modules/${moduleId}/complete`,
      payload,
    )

    const body = response.data
    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to complete module')
    }

    return ModuleProgressModel.fromJSON(body.data)
  }
}

export const learnerCoursesService = new LearnerCoursesService()
